/**
 * Cross-platform `getUserLocation` replacement.
 *
 * Why: the legacy `www/modals/geolocation.js` uses
 * `navigator.geolocation.getCurrentPosition` directly. On Capacitor Android
 * (targetSdk 35, edge-to-edge WebView) that browser API is unreliable — it
 * often times out or returns nothing even with location permission granted.
 *
 * The Capacitor `@capacitor/geolocation` plugin talks to FusedLocationProvider
 * natively and returns a real fix consistently. We use it whenever we're on
 * a native platform, and fall back to `navigator.geolocation` for the
 * regular browser build (where the Capacitor plugin doesn't exist).
 *
 * Returns the same shape the legacy code emitted so every caller (weather
 * card, services list, plate scanner, account drawer, etc.) keeps working
 * without modification:
 *
 *   { lat, lon, latitude, longitude, accuracy, timestamp }
 */

import { createLogger } from './logger.js';

const log = createLogger('geolocation');

const DEFAULT_TIMEOUT_MS = 8000;
const DEFAULT_MAX_AGE_MS = 60_000;

/**
 * @param {{ timeout?: number; maxAge?: number; highAccuracy?: boolean }} [options]
 * @returns {Promise<{ lat: number; lon: number; latitude: number; longitude: number; accuracy: number; timestamp: number }>}
 */
export async function getUserLocation(options = {}) {
  const timeout = options.timeout ?? DEFAULT_TIMEOUT_MS;
  const maxAge = options.maxAge ?? DEFAULT_MAX_AGE_MS;
  const highAccuracy = options.highAccuracy ?? false;

  const cap = /** @type {any} */ (window).Capacitor;
  const isNative = !!cap?.isNativePlatform?.();

  if (isNative) {
    const plugin = cap?.Plugins?.Geolocation;
    if (!plugin) {
      log.warn('Capacitor.Plugins.Geolocation missing — falling back to navigator.geolocation');
    } else {
      try {
        // The plugin handles permissions internally on Android — no extra
        // call needed, but we explicitly check so the user sees the system
        // prompt rather than a silent denial on first invocation.
        const perm = await plugin.checkPermissions();
        if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
          const req = await plugin.requestPermissions({ permissions: ['location'] });
          if (req.location !== 'granted' && req.coarseLocation !== 'granted') {
            throw new Error('location permission denied');
          }
        }
        const pos = await plugin.getCurrentPosition({
          enableHighAccuracy: highAccuracy,
          timeout,
          maximumAge: maxAge,
        });
        return shape(pos);
      } catch (err) {
        log.warn('Capacitor Geolocation failed:', /** @type {Error} */ (err).message);
        // Fall through to the browser API as a last resort.
      }
    }
  }

  return webFallback({ timeout, maxAge, highAccuracy });
}

/**
 * @param {{ timeout: number; maxAge: number; highAccuracy: boolean }} opts
 * @returns {Promise<{ lat: number; lon: number; latitude: number; longitude: number; accuracy: number; timestamp: number }>}
 */
function webFallback({ timeout, maxAge, highAccuracy }) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(shape(position)),
      (err) => reject(err),
      { enableHighAccuracy: highAccuracy, timeout, maximumAge: maxAge },
    );
  });
}

/**
 * Normalise the position object into the shape every caller expects.
 * Both the Capacitor plugin AND the browser API return `{ coords, timestamp }`,
 * so a single converter handles both.
 *
 * @param {any} pos
 */
function shape(pos) {
  const lat = pos?.coords?.latitude ?? pos?.lat ?? 0;
  const lon = pos?.coords?.longitude ?? pos?.lon ?? 0;
  return {
    lat,
    lon,
    latitude: lat,
    longitude: lon,
    accuracy: pos?.coords?.accuracy ?? 0,
    timestamp: pos?.timestamp ?? Date.now(),
  };
}
